import React, { Fragment, useMemo } from 'react';
import { ItemBody, Section, SectionBody, SectionHeader, Separator, WrapperTitle, WrapperSubtitle } from './SectionSummary.styles';
import { TextBlock, TypeOfText } from '../text/TextBlock';
import { useAppSelector } from '@app/hooks';
import { formatToTitleCase } from '@/utils/formatToLabelCase';
import { parseSummaryValues } from '@/utils/parseInputValue';
import { formatCurrency } from '@/utils/formatValues';

interface SummaryItem {
  key: string;
  value: string;
}

interface SummarySection {
  section: string;
  isHidden: boolean;
  items: SummaryItem[];
}

interface Summary {
  title: {
    isHidden: boolean;
    text: string;
  };
  applicant: {
    isHidden: boolean;
    text: string;
  };
  subTitle: {
    isHidden: boolean;
    text: string;
  };
  consumer: {
    isHidden: boolean;
    text: string;
  };
  summaryContainer: SummarySection[];
}

interface SectionSummaryProps {
  summary: Summary;
  isCoBorrower?: boolean;
}

const calculateTotalIncome = (borrower: any): number => {
  const total =
    borrower.otherIncome?.reduce((total: number, income: any) => {
      return total + (income.grossMonthlyIncome || 0);
    }, 0) || 0;
  return total;
};

const createBaseItems = (employment: any, isFirstEmployment: boolean): SummaryItem[] => {
  return [
    ...(isFirstEmployment ? [{ key: 'title', value: 'Employment Details' }] : [{ key: 'title', value: '' }]),
    { key: 'employer', value: employment.employerName },
    { key: 'joined', value: employment.startDate },
    { key: 'totalMonths', value: employment.totalMonths ? employment.totalMonths.toString() : '' },
    ...(employment.currentEmployment ? [{ key: 'monthlyIncome', value: formatCurrency(employment.grossMonthlyIncome) || '' }] : []),
  ];
};

const SectionSummary: React.FC<SectionSummaryProps> = ({ summary, isCoBorrower }) => {
  const loanApplication = useAppSelector((state) => state.LoanApplication);
  const templateConfig = useAppSelector((state) => state.TemplateConfig);

  const { title, subTitle, summaryContainer, applicant } = summary;
  const isApplicant = applicant?.text === 'Applicant' || applicant?.text === 'Consumer';
  const borrowerIndex = isApplicant ? 0 : 1;
  const employer = loanApplication?.borrowers[borrowerIndex]?.employmentHistory.length;
  const hasDrawAmountPage = templateConfig?.pages?.hasDrawAmountPage;
  const isICE = templateConfig?.isICE ?? false;

  const coApplicantLabel = templateConfig?.pages?.personalInformation?.formInputs?.Coborrower?.coApplicantLabel || 'Co-Consumer';

  // NR masking configuration
  const shouldApplyMasking = !(templateConfig?.pages?.features as any)?.newRelic?.unmaskSensitiveFields;

  // sensitive field keys that should be masked
  const sensitiveFields = ['birthday', 'dateOfBirth', 'dob', 'home', 'homePhone', 'cell', 'cellPhone', 'mobilePhone', 'work', 'workPhone', 'ethnicity', 'race', 'gender', 'sex'];

  // section names that should be blocked (HMDA sections)
  const sensitiveSection = (sectionTitle: string) => {
    const hmDASections = ['hmda', 'government monitoring', 'ethnicity', 'race', 'gender'];
    return hmDASections.some((section) => sectionTitle.toLowerCase().includes(section.toLowerCase()));
  };

  const employmentSections = useMemo(() => {
    return (
      loanApplication?.borrowers[borrowerIndex]?.employmentHistory?.map((employment: any, index: number) => {
        const isLastEmployment = index + 1 === employer;
        const baseItems = createBaseItems(employment, index === 0);
        if (!isLastEmployment) {
          return {
            section: `employment_${index}`,
            items: baseItems,
            isHidden: false,
          };
        }

        const totalEmploymentIncome = loanApplication?.borrowers[borrowerIndex]?.employmentHistory
          .filter((emp) => emp.currentEmployment)
          .reduce((sum, emp) => {
            return sum + (emp.grossMonthlyIncome || 0);
          }, 0);

        const totalAdditionalIncome = calculateTotalIncome(loanApplication?.borrowers[borrowerIndex]);

        const additionalIncomes =
          loanApplication?.borrowers[borrowerIndex]?.otherIncome?.map((income) => ({
            key: `otherIncome_${income.incomeType}`,
            value: `${income.incomeType}`,
          })) || [];

        return {
          section: `employment_${index}`,
          isHidden: false,
          items: [
            ...baseItems,
            ...additionalIncomes,
            {
              key: 'totalMonthlyIncome',
              value: formatCurrency(totalEmploymentIncome + totalAdditionalIncome),
            },
          ],
        };
      }) || []
    );
  }, [loanApplication?.borrowers, borrowerIndex, employer]);

  const updatedSummaryContainer = useMemo(() => {
    const employmentIndex = summaryContainer?.findIndex((section) => section.section === 'employment');
    if (employmentIndex === -1) return summaryContainer;

    const processedContainer = [...summaryContainer.slice(0, employmentIndex), ...employmentSections, ...summaryContainer.slice(employmentIndex + 1)];

    const isVmoV2 = templateConfig?.features?.vmoRedesign?.isEnabled ?? false;
    if (!hasDrawAmountPage && !isVmoV2) {
      return processedContainer.map((section) => {
        if (section.section === 'loanDetails') {
          return {
            ...section,
            items: section.items.filter((item) => item.key !== 'initialDrawAmount'),
          };
        }
        return section;
      });
    }

    return processedContainer;
  }, [summaryContainer, employmentSections, hasDrawAmountPage]);

  const renderHeaderSection = () => {
    if (!isApplicant) return null;

    return (
      <>
        <WrapperTitle>
          <TextBlock text={title.text || 'Review Your Inquiry'} direction={'flex-start'} color="default" type={TypeOfText.H2} isHidden={title?.isHidden} />
        </WrapperTitle>
        <WrapperSubtitle>
          <TextBlock
            text={subTitle.text || 'Please review your application information for accuracy.'}
            direction={'flex-start'}
            color="default"
            type={TypeOfText.Subtitle}
            isHidden={subTitle?.isHidden}
          />
        </WrapperSubtitle>
      </>
    );
  };

  const renderApplicantInfo = () => {
    const text = isCoBorrower
      ? applicant.text?.replace('%%coApplicantLabel%%', coApplicantLabel) || 'Co-Applicant'
      : `${applicant.text}: ${loanApplication.borrowers[0]?.borrowerFirstName} ${loanApplication.borrowers[0]?.borrowerLastName}`;

    return <TextBlock text={text} direction={'flex-start'} color="default" type={TypeOfText.H2} isHidden={applicant?.isHidden} />;
  };

  const renderSummaryItem = (item: SummaryItem, idx: number, sectionData: SummaryItem[]) => {
    const displayValue = item.value === '%%drawAmount%%' ? '%%initialDrawAmount%%' : item.value;

    //check if this item contains sensitive information
    const isSensitiveField = sensitiveFields.some((field) => item.key.toLowerCase().includes(field.toLowerCase()));
    const maskingClass = shouldApplyMasking && isSensitiveField ? 'nr-block' : '';

    if (item.key === 'preferredContact') {
      const parsed = parseSummaryValues(displayValue, loanApplication);

      const options = (templateConfig?.pages?.personalInformation?.formInputs?.contactMethod?.options || []) as Array<{ label: string; beValue: string }>;
      const normalizedParsed = (parsed || '').toString().toLowerCase();
      const matched = options.find((opt) => (opt.beValue || '').toLowerCase() === normalizedParsed || (opt.label || '').toLowerCase() === normalizedParsed);
      const displayText = matched?.label || (parsed ? formatToTitleCase(parsed) : '');

      return (
        <ItemBody key={idx} $isBasicInformation={false} className={maskingClass}>
          <TextBlock text={`${formatToTitleCase(item.key)}:` || 'Unknown'} direction="flex-start" width="30%" type={TypeOfText.Subtitle} />
          <TextBlock text={displayText} direction="flex-end" color="default" width="70%" type={TypeOfText.Subtitle} />
        </ItemBody>
      );
    }

    if (item.key === 'cell' || item.key === 'home' || item.key === 'work') {
      const parsed = parseSummaryValues(displayValue, loanApplication);

      const configuredLabel =
        item.key === 'cell'
          ? templateConfig?.pages?.personalInformation?.formInputs?.mobilePhone?.label || 'Mobile Phone'
          : item.key === 'home'
            ? templateConfig?.pages?.personalInformation?.formInputs?.homePhone?.label || 'Home Phone'
            : templateConfig?.pages?.personalInformation?.formInputs?.workPhone?.label || 'Work Phone';

      return (
        <ItemBody key={idx} $isBasicInformation={false} className={maskingClass}>
          <TextBlock text={`${configuredLabel}:`} direction="flex-start" width="30%" type={TypeOfText.Subtitle} />
          <TextBlock text={parsed} direction="flex-end" color="default" width="70%" type={TypeOfText.Subtitle} />
        </ItemBody>
      );
    }

    if (item.key === 'totalMonthlyIncome' || item.key.startsWith('otherIncome_')) {
      const isFirstIncome =
        item.key.startsWith('otherIncome_') && !sectionData.slice(0, idx).some((prevItem) => prevItem.key.startsWith('otherIncome_') || prevItem.key === 'totalMonthlyIncome');

      // Only show total monthly income if there are multiple employments or additional income
      const hasMultipleEmployments = employer > 1;
      const hasAdditionalIncome = sectionData.some((prevItem) => prevItem.key.startsWith('otherIncome_'));

      if (item.key === 'totalMonthlyIncome' && !hasMultipleEmployments && !hasAdditionalIncome) {
        return null;
      }
      return (
        <Fragment key={idx}>
          {isFirstIncome && <Separator $isTotalMonths={true} />}
          <ItemBody $isTotalMonths={true} className={maskingClass}>
            <TextBlock text={item.key === 'totalMonthlyIncome' ? 'Total Monthly Income:' : 'Additional Income:'} direction="flex-start" width="30%" type={TypeOfText.Subtitle} />
            <TextBlock text={parseSummaryValues(displayValue, loanApplication)} direction="flex-end" color="default" width="70%" type={TypeOfText.Subtitle} />
          </ItemBody>
        </Fragment>
      );
    }

    if (item.key === 'title') return null;

    if ((item.key === 'lineOfCredit' && loanApplication.selectedProgramType === 'Home Equity Loan') || (item.key === 'lineOfCredit' && isICE)) {
      return (
        <ItemBody key={idx} $isBasicInformation={false} className={maskingClass}>
          <TextBlock text="Loan Amount:" direction="flex-start" width="30%" type={TypeOfText.Subtitle} />
          <TextBlock text={parseSummaryValues(displayValue, loanApplication)} direction="flex-end" color="default" width="70%" type={TypeOfText.Subtitle} />
        </ItemBody>
      );
    }

    return (
      <ItemBody key={idx} $isBasicInformation={false} className={maskingClass}>
        <TextBlock text={`${formatToTitleCase(item.key)}:` || 'Unknown'} direction="flex-start" width="30%" type={TypeOfText.Subtitle} />
        <TextBlock text={parseSummaryValues(displayValue, loanApplication)} direction="flex-end" color="default" width="70%" type={TypeOfText.Subtitle} />
      </ItemBody>
    );
  };

  return (
    <>
      {renderHeaderSection()}
      {renderApplicantInfo()}
      {updatedSummaryContainer?.map((section, index) => {
        if (section.isHidden) return null;
        const sectionData = section.items;
        const fullWidth = index !== 0;

        // get section title and check if it's a sensitive section
        const sectionTitle = sectionData.find((item) => item.key === 'title')?.value || '';
        const isSensitiveSection = sensitiveSection(sectionTitle);
        const sectionMaskingClass = shouldApplyMasking && isSensitiveSection ? 'nr-block' : '';

        return (
          <Fragment key={index}>
            <Separator />
            <Section className={sectionMaskingClass}>
              <SectionHeader>
                <TextBlock text={sectionTitle} direction={'flex-start'} color="default" type={TypeOfText.H3} />
              </SectionHeader>
              <SectionBody $fullWidth={fullWidth}>{sectionData?.map((item, idx) => renderSummaryItem(item, idx, sectionData))}</SectionBody>
            </Section>
          </Fragment>
        );
      })}
    </>
  );
};

export default React.memo(SectionSummary);
